import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Clock,
  CheckCircle2,
  AlertCircle,
  Plus,
  Circle,
  RefreshCw,
  Building2,
} from "lucide-react";
import { Button } from "../../../components/ui/Button";
import { Modal } from "../../../components/ui/Modal";
import { UserPicker, type SelectedUser } from "../../../components/ui/UserPicker";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { useToast } from "../../../hooks/useToast";
import { ToastContainer } from "../../../components/ui/Toast";
import { useBizTasks, useBizCreateTask } from "../../../hooks/useBizAdmin";
import { useAuth } from "../../../hooks/useAuth";
import type { Task, TaskStatus, TaskPriority } from "../../../lib/taskApi";
import { formatDate } from "../../../utils/format";

// ── Helpers ───────────────────────────────────────────────────────────────────

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  HIGH: "text-[#dc2626] bg-[#fef2f2]",
  MEDIUM: "text-[#d97706] bg-[#fffbeb]",
  LOW: "text-[#64748b] bg-[#f1f5f9]",
};

const COLUMN_CONFIG: Array<{
  status: TaskStatus;
  label: string;
  icon: React.ReactNode;
  headerColor: string;
}> = [
  { status: "TODO", label: "To Do", icon: <Circle className="h-4 w-4" />, headerColor: "text-[#64748b]" },
  { status: "IN_PROGRESS", label: "In Progress", icon: <Clock className="h-4 w-4" />, headerColor: "text-[#2447d9]" },
  { status: "BLOCKED", label: "Blocked", icon: <AlertCircle className="h-4 w-4" />, headerColor: "text-[#dc2626]" },
  { status: "DONE", label: "Done", icon: <CheckCircle2 className="h-4 w-4" />, headerColor: "text-[#16a34a]" },
];

// ── Create Task Form ──────────────────────────────────────────────────────────

const createTaskSchema = z.object({
  title: z.string().min(1, "Title is required").max(200),
  description: z.string().max(2000).default(""),
  priority: z.enum(["LOW", "MEDIUM", "HIGH"]).default("MEDIUM"),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD")
    .optional()
    .or(z.literal("")),
});

type CreateTaskForm = z.infer<typeof createTaskSchema>;

interface CreateTaskModalProps {
  onClose: () => void;
}

const IC = "w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:border-transparent";

function CreateTaskModal({ onClose }: CreateTaskModalProps) {
  const { toasts, toast, dismissToast } = useToast();
  const { activeBusinessId } = useAuth();
  const createTask = useBizCreateTask(activeBusinessId ?? "");
  const [assignee, setAssignee] = useState<SelectedUser | null>(null);
  const [assigneeError, setAssigneeError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateTaskForm>({
    resolver: zodResolver(createTaskSchema),
    defaultValues: { priority: "MEDIUM", description: "" },
  });

  const onSubmit = async (data: CreateTaskForm) => {
    if (!assignee) {
      setAssigneeError("Please select an employee.");
      return;
    }
    setAssigneeError("");
    try {
      await createTask.mutateAsync({
        assignedTo: assignee.userId,
        title: data.title,
        description: data.description,
        priority: data.priority as TaskPriority,
        dueDate: data.dueDate || undefined,
      });
      toast.success("Task created and assigned successfully.");
      setTimeout(onClose, 1000);
    } catch {
      toast.error("Failed to create task. Please try again.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-[#0f172a]">
            Assign to <span className="text-[#dc2626]">*</span>
          </label>
          <UserPicker
            value={assignee}
            onChange={(u) => { setAssignee(u); if (u) setAssigneeError(""); }}
            placeholder="Search and select employee…"
            error={assigneeError}
            businessId={activeBusinessId ?? undefined}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="title" className="text-sm font-medium text-[#0f172a]">Title <span className="text-[#dc2626]">*</span></label>
          <input id="title" {...register("title")} placeholder="Brief task title" className={IC} />
          {errors.title && <p className="text-xs text-[#dc2626]">{errors.title.message}</p>}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="description" className="text-sm font-medium text-[#0f172a]">Description <span className="text-[#dc2626]">*</span></label>
          <textarea id="description" {...register("description")} placeholder="Detailed instructions or context…" rows={3} className={`${IC} resize-none`} />
          {errors.description && <p className="text-xs text-[#dc2626]">{errors.description.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="priority" className="text-sm font-medium text-[#0f172a]">Priority</label>
            <select id="priority" {...register("priority")} className={IC}>
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="dueDate" className="text-sm font-medium text-[#0f172a]">Due Date (optional)</label>
            <input id="dueDate" type="date" {...register("dueDate")} className={IC} />
            {errors.dueDate && <p className="text-xs text-[#dc2626]">{errors.dueDate.message}</p>}
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} fullWidth>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} fullWidth>
            {isSubmitting ? "Creating…" : "Create & Assign Task"}
          </Button>
        </div>
      </form>
    </>
  );
}

// ── Task Card ─────────────────────────────────────────────────────────────────

function TaskCard({ task }: { task: Task }) {
  return (
    <div className="rounded-xl border border-[#e2e8f0] bg-white px-4 py-3 shadow-[0_1px_2px_0_rgb(0_0_0/0.05)] hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm font-medium text-[#0f172a] leading-snug line-clamp-2">{task.title}</p>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${PRIORITY_COLOR[task.priority]}`}
        >
          {task.priority}
        </span>
      </div>

      {task.assigneeName && (
        <p className="text-xs text-[#64748b] mb-2">
          <span className="text-[#94a3b8]">Assigned to</span> {task.assigneeName}
        </p>
      )}

      {task.description && (
        <p className="text-xs text-[#94a3b8] line-clamp-2 mb-2">{task.description}</p>
      )}

      <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#f1f5f9]">
        {task.dueDate ? (
          <span className="text-xs text-[#94a3b8]">Due {formatDate(task.dueDate)}</span>
        ) : (
          <span />
        )}
        {task.updates.length > 0 && (
          <span className="text-xs text-[#94a3b8]">
            {task.updates.length} update{task.updates.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="mt-2 pt-2 border-t border-[#f1f5f9] flex items-center justify-between gap-2 text-[10px] text-[#94a3b8]">
        <span>Created {formatDate(task.createdAt)}</span>
        {task.createdBy && <span>by {task.createdBy}</span>}
      </div>
    </div>
  );
}

// ── Kanban Column ─────────────────────────────────────────────────────────────

interface KanbanColumnProps {
  status: TaskStatus;
  label: string;
  icon: React.ReactNode;
  headerColor: string;
}

function KanbanColumn({ status, label, icon, headerColor }: KanbanColumnProps) {
  const { activeBusinessId } = useAuth();
  const bizId = activeBusinessId ?? "";
  const { isLoading, refetch, data } = useBizTasks(bizId, status);
  const tasks: Task[] = data ?? [];

  return (
    <div className="flex flex-col min-h-[400px]">
      <div className={`flex items-center justify-between mb-3 ${headerColor}`}>
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-semibold">{label}</span>
          <span className="ml-1 rounded-full bg-[#f1f5f9] px-2 py-0.5 text-xs font-medium text-[#64748b]">
            {tasks.length}
          </span>
        </div>
        <button
          onClick={() => refetch()}
          className="text-[#94a3b8] hover:text-[#64748b] transition-colors"
          title="Refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex-1 space-y-3">
        {isLoading && (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        )}

        {!isLoading && tasks.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#e2e8f0] py-8 text-center">
            <p className="text-xs text-[#94a3b8]">No tasks here</p>
          </div>
        )}

        {!isLoading && tasks.map((task) => <TaskCard key={task.taskId} task={task} />)}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminTasksPage() {
  const [showCreate, setShowCreate] = useState(false);
  const { toasts, dismissToast } = useToast();
  const { activeBusinessId } = useAuth();

  if (!activeBusinessId) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-[#e2e8f0] bg-white py-24 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#eff3ff]">
          <Building2 className="h-8 w-8 text-[#2447d9]" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-[#0f172a]">No business selected</h2>
          <p className="mt-1 text-sm text-[#64748b]">Select a business from the top bar to manage tasks.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0f172a]">Manage Tasks</h1>
          <p className="mt-1 text-sm text-[#64748b]">Create and assign tasks to employees.</p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" />
          New Task
        </Button>
      </div>

      {/* Kanban board */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {COLUMN_CONFIG.map((col) => (
          <div key={col.status} className="rounded-2xl border border-[#e2e8f0] bg-[#f8fafc] p-4">
            <KanbanColumn
              status={col.status}
              label={col.label}
              icon={col.icon}
              headerColor={col.headerColor}
            />
          </div>
        ))}
      </div>

      {/* Create Task Modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create & Assign Task"
        size="md"
      >
        <CreateTaskModal onClose={() => setShowCreate(false)} />
      </Modal>
    </div>
  );
}
